/**
 * 管理者用問題一覧ページ
 * 全問題の問題文・選択肢・正解・基本解説・詳細解説を一覧表示
 */

import React, { useState, useMemo, useCallback } from "react";
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Loader2,
  AlertCircle,
  RefreshCw,
  Image as ImageIcon,
  ImageOff,
  Users,
  Ticket,
  Copy,
  Ban,
  Crown,
  Flag,
  Sparkles,
} from "lucide-react";
import { useQuestions, QuestionFilter } from "../../../hooks/useQuestions";
import { useAuth } from "../../../contexts/AuthContext";
import { Question } from "../../../types";
import { Button } from "../../ui/Button";
import { GeneratedPanel } from "./GeneratedPanel";
import {
  EXAM_TYPES,
  EXAM_YEARS as YEARS,
  EXAM_SEASONS as SEASONS,
} from "../../../constants/examCatalog";

const CHOICE_LABELS = ["ア", "イ", "ウ", "エ"];
const PAGE_SIZE = 20;

type FigureFilter = "all" | "figure_all" | "figure_a" | "figure_b" | "has_image" | "no_image";

/** 選択肢が図かどうかを判定 */
function choicesAreFigure(choices: string[]): boolean {
  return choices.some(
    (c) =>
      c.includes("グラフ省略") ||
      c.includes("図省略") ||
      c.includes("表省略") ||
      c.includes("波形図省略")
  ) ||
  (choices.length === 4 &&
    choices.every((c) => /^[A-D]$/.test(c.trim()))) ||
  (choices.length === 4 &&
    choices.every((c) => /^根\d/.test(c.trim())));
}

/** 図問題かどうか（テキスト・選択肢・image_url いずれかで判定） */
function hasFigurePlaceholder(q: Question): boolean {
  const textHas =
    q.questionText.includes("図省略") ||
    q.questionText.includes("グラフ省略") ||
    q.questionText.includes("表省略");
  const choicesHas = q.choices.some(
    (c) =>
      c.includes("図省略") ||
      c.includes("グラフ省略") ||
      c.includes("表省略") ||
      c.includes("波形図省略")
  );
  return textHas || choicesHas || !!q.imageUrl || !!q.choiceImageUrl;
}

/**
 * 展開可能な問題行コンポーネント
 */
const QuestionRow: React.FC<{ question: Question; index: number; defaultExpanded?: boolean }> = ({
  question,
  index,
  defaultExpanded = false,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const correctLabel =
    question.correctAnswer >= 0 && question.correctAnswer <= 3
      ? CHOICE_LABELS[question.correctAnswer]
      : "?";

  return (
    <div className="border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800">
      {/* サマリー行 */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-slate-50 dark:hover:bg-slate-900 transition-colors"
      >
        <span className="text-xs text-slate-500 dark:text-slate-400 w-8 shrink-0 text-right">
          {index + 1}
        </span>
        <span className="text-xs font-mono text-slate-500 dark:text-slate-400 w-48 shrink-0 truncate">
          {question.id}
        </span>
        <span className="text-sm text-slate-900 dark:text-slate-100 flex-1 truncate">
          {question.questionText}
        </span>
        <span className="text-xs font-bold text-sky-700 w-10 shrink-0 text-center">
          {correctLabel}
        </span>
        <span
          className={`text-xs w-12 shrink-0 text-center ${question.basicExplanation ? "text-green-600" : "text-red-400"}`}
        >
          {question.basicExplanation ? "基本" : "-"}
        </span>
        <span
          className={`text-xs w-12 shrink-0 text-center ${question.detailedExplanation ? "text-green-600" : "text-red-400"}`}
        >
          {question.detailedExplanation ? "詳細" : "-"}
        </span>
        <span
          className={`text-xs w-10 shrink-0 text-center ${
            !hasFigurePlaceholder(question)
              ? "text-transparent"
              : (question.imageUrl || question.choiceImageUrl) ? "text-green-600" : "text-orange-500"
          }`}
          title={
            !hasFigurePlaceholder(question)
              ? ""
              : (question.imageUrl || question.choiceImageUrl)
                ? "画像設定済み"
                : choicesAreFigure(question.choices)
                  ? "Type B: 選択肢も図"
                  : "Type A: 問題文の図のみ"
          }
        >
          {hasFigurePlaceholder(question) ? (
            (question.imageUrl || question.choiceImageUrl) ? (
              <ImageIcon className="w-4 h-4 inline" />
            ) : (
              <ImageOff className="w-4 h-4 inline" />
            )
          ) : null}
        </span>
        {expanded ? (
          <ChevronUp className="w-4 h-4 text-slate-500 dark:text-slate-400 shrink-0" />
        ) : (
          <ChevronDown className="w-4 h-4 text-slate-500 dark:text-slate-400 shrink-0" />
        )}
      </button>

      {/* 展開時の詳細 */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-slate-200 dark:border-slate-600 pt-3 space-y-3">
          {/* 図表ステータス */}
          {hasFigurePlaceholder(question) && (
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium ${
              (question.imageUrl || question.choiceImageUrl)
                ? "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800"
                : "bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400 border border-orange-200 dark:border-orange-800"
            }`}>
              {(question.imageUrl || question.choiceImageUrl) ? (
                <ImageIcon className="w-4 h-4" />
              ) : (
                <ImageOff className="w-4 h-4" />
              )}
              <span>
                {choicesAreFigure(question.choices) ? "Type B: 選択肢も図" : "Type A: 問題文の図のみ"}
                {question.imageUrl ? " ✓ 問題図あり" : ""}
                {question.choiceImageUrl ? " ✓ 選択肢図あり" : ""}
                {!question.imageUrl && !question.choiceImageUrl ? " — 画像未設定" : ""}
              </span>
            </div>
          )}

          {/* 画像プレビュー */}
          {question.imageUrl && (
            <div>
              <h4 className="text-xs font-bold text-slate-500 dark:text-slate-400 mb-1">問題図</h4>
              <img
                src={question.imageUrl}
                alt={`問${question.questionNumber} の図`}
                className="max-w-full h-auto rounded-lg border border-slate-200 dark:border-slate-600"
                loading="lazy"
              />
            </div>
          )}
          {question.choiceImageUrl && (
            <div>
              <h4 className="text-xs font-bold text-slate-500 dark:text-slate-400 mb-1">選択肢図</h4>
              <img
                src={question.choiceImageUrl}
                alt={`問${question.questionNumber} の選択肢図`}
                className="max-w-full h-auto rounded-lg border border-slate-200 dark:border-slate-600"
                loading="lazy"
              />
            </div>
          )}

          {/* 問題文 */}
          <div>
            <h4 className="text-xs font-bold text-slate-500 dark:text-slate-400 mb-1">問題文</h4>
            <p className="text-sm text-slate-900 dark:text-slate-100 whitespace-pre-wrap">
              {question.questionText}
            </p>
          </div>

          {/* 選択肢 */}
          <div>
            <h4 className="text-xs font-bold text-slate-500 dark:text-slate-400 mb-1">選択肢</h4>
            <div className="space-y-1">
              {question.choices.map((choice, i) => (
                <div
                  key={i}
                  className={`text-sm px-2 py-1 rounded ${
                    i === question.correctAnswer
                      ? "bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-300 font-medium border border-green-200 dark:border-green-800"
                      : "text-slate-700 dark:text-slate-300"
                  }`}
                >
                  <span className="font-bold mr-2">{CHOICE_LABELS[i]}</span>
                  {choice}
                </div>
              ))}
            </div>
          </div>

          {/* 正解 */}
          <div>
            <h4 className="text-xs font-bold text-slate-500 dark:text-slate-400 mb-1">正解</h4>
            <span className="text-sm font-bold text-sky-700">
              {correctLabel}
            </span>
          </div>

          {/* 基本解説 */}
          <div>
            <h4 className="text-xs font-bold text-slate-500 dark:text-slate-400 mb-1">
              基本解説 (Sonnet)
            </h4>
            {question.basicExplanation ? (
              <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap bg-blue-50 dark:bg-blue-900/20 p-2 rounded border border-blue-100 dark:border-blue-800">
                {question.basicExplanation}
              </p>
            ) : (
              <p className="text-sm text-red-400 italic">未設定</p>
            )}
          </div>

          {/* 詳細解説 */}
          <div>
            <h4 className="text-xs font-bold text-slate-500 dark:text-slate-400 mb-1">
              詳細解説 (Opus)
            </h4>
            {question.detailedExplanation ? (
              <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap bg-amber-50 p-2 rounded border border-amber-100">
                {question.detailedExplanation}
              </p>
            ) : (
              <p className="text-sm text-red-400 italic">未設定</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

type AdminTab = "users" | "questions" | "feedback" | "generated";

/** Dummy user type for skeleton UI */
interface DummyUser {
  id: string;
  email: string;
  tier: "free" | "pro";
  createdAt: string;
  lastLogin: string;
}

/** Invite code type for state management */
interface InviteCode {
  code: string;
  tier: string;
  expiresAt: string | null;
  maxUses: number;
  usedCount: number;
  createdAt: string;
  disabled?: boolean;
}

function generateCode(): string {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let result = "KAKUMON-";
  for (let i = 0; i < 8; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

const DUMMY_USERS: DummyUser[] = [
  { id: "1", email: "user1@example.com", tier: "pro", createdAt: "2026-03-01", lastLogin: "2026-04-03" },
  { id: "2", email: "user2@example.com", tier: "free", createdAt: "2026-03-15", lastLogin: "2026-04-02" },
  { id: "3", email: "test@example.com", tier: "free", createdAt: "2026-03-20", lastLogin: "2026-04-01" },
];

/**
 * User Management Section (skeleton with dummy data)
 */
const UserManagementSection: React.FC = () => {
  const [users, setUsers] = useState<DummyUser[]>(DUMMY_USERS);

  const toggleTier = useCallback((userId: string) => {
    setUsers((prev) =>
      prev.map((u) =>
        u.id === userId
          ? { ...u, tier: u.tier === "pro" ? "free" : "pro" }
          : u
      )
    );
  }, []);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
          <Users className="w-5 h-5" />
          ユーザー管理
          {/* L-6: モック解消はスコープ外。DUMMY_USERSであることを明示するバッジのみ追加 */}
          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">
            モック表示
          </span>
        </h3>
        <span className="text-sm text-slate-500 dark:text-slate-400">{users.length} ユーザー (dummy)</span>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-600 overflow-hidden">
        {/* Header */}
        <div className="hidden md:grid grid-cols-[1fr_80px_100px_100px_100px] gap-3 px-4 py-2 text-xs font-bold text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-900">
          <span>メール</span>
          <span className="text-center">プラン</span>
          <span className="text-center">登録日</span>
          <span className="text-center">最終ログイン</span>
          <span className="text-center">操作</span>
        </div>

        {users.map((u) => (
          <div
            key={u.id}
            className="grid grid-cols-1 md:grid-cols-[1fr_80px_100px_100px_100px] gap-2 md:gap-3 px-4 py-3 border-b border-slate-200 dark:border-slate-600 last:border-b-0 items-center"
          >
            <span className="text-sm text-slate-900 dark:text-slate-100 truncate">{u.email}</span>
            <span className="md:text-center">
              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                u.tier === "pro"
                  ? "bg-blue-100 text-blue-700"
                  : "bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400"
              }`}>
                {u.tier === "pro" ? "Pro" : "Free"}
              </span>
            </span>
            <span className="text-xs text-slate-500 dark:text-slate-400 md:text-center">{u.createdAt}</span>
            <span className="text-xs text-slate-500 dark:text-slate-400 md:text-center">{u.lastLogin}</span>
            <span className="md:text-center">
              <button
                onClick={() => toggleTier(u.id)}
                className={`text-xs font-medium px-2.5 py-1 rounded-lg transition-colors ${
                  u.tier === "pro"
                    ? "bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30"
                    : "bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30"
                }`}
              >
                {u.tier === "pro" ? "Pro解除" : "Pro付与"}
              </button>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Invite Code Management Section
 */
const InviteCodeSection: React.FC = () => {
  const [codes, setCodes] = useState<InviteCode[]>([
    {
      code: "KAKUMON-BETA-A1B2",
      tier: "pro",
      expiresAt: "2026-06-30",
      maxUses: 10,
      usedCount: 3,
      createdAt: "2026-03-29",
    },
  ]);

  const handleGenerate = useCallback(() => {
    const newCode: InviteCode = {
      code: generateCode(),
      tier: "pro",
      expiresAt: null,
      maxUses: 1,
      usedCount: 0,
      createdAt: new Date().toISOString().split("T")[0],
    };
    setCodes((prev) => [newCode, ...prev]);
  }, []);

  const handleDisable = useCallback((code: string) => {
    setCodes((prev) =>
      prev.map((c) => (c.code === code ? { ...c, disabled: true } : c))
    );
  }, []);

  const handleCopy = useCallback((code: string) => {
    navigator.clipboard.writeText(code);
  }, []);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
          <Ticket className="w-5 h-5" />
          招待コード管理
        </h3>
        <button
          onClick={handleGenerate}
          className="px-4 py-2 text-sm font-medium rounded-lg bg-slate-900 dark:bg-slate-100 dark:bg-slate-700 text-white dark:text-slate-900 dark:text-slate-100 hover:bg-slate-800 dark:hover:bg-slate-200 transition-colors"
        >
          新規コード発行
        </button>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-600 overflow-hidden">
        {/* Header */}
        <div className="hidden md:grid grid-cols-[1fr_60px_100px_80px_100px_80px] gap-3 px-4 py-2 text-xs font-bold text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-900">
          <span>コード</span>
          <span className="text-center">Tier</span>
          <span className="text-center">有効期限</span>
          <span className="text-center">使用状況</span>
          <span className="text-center">作成日</span>
          <span className="text-center">操作</span>
        </div>

        {codes.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
            招待コードがありません
          </div>
        ) : (
          codes.map((c) => (
            <div
              key={c.code}
              className={`grid grid-cols-1 md:grid-cols-[1fr_60px_100px_80px_100px_80px] gap-2 md:gap-3 px-4 py-3 border-b border-slate-200 dark:border-slate-600 last:border-b-0 items-center ${
                c.disabled ? "opacity-50" : ""
              }`}
            >
              <div className="flex items-center gap-2">
                <code className="text-sm font-mono text-slate-900 dark:text-slate-100">{c.code}</code>
                <button
                  onClick={() => handleCopy(c.code)}
                  className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                  title="コピー"
                >
                  <Copy className="w-3.5 h-3.5 text-slate-500 dark:text-slate-400" />
                </button>
              </div>
              <span className="md:text-center">
                <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                  {c.tier}
                </span>
              </span>
              <span className="text-xs text-slate-500 dark:text-slate-400 md:text-center">
                {c.expiresAt || "無期限"}
              </span>
              <span className="text-xs text-slate-500 dark:text-slate-400 md:text-center">
                {c.usedCount}/{c.maxUses}
              </span>
              <span className="text-xs text-slate-500 dark:text-slate-400 md:text-center">{c.createdAt}</span>
              <span className="md:text-center">
                {c.disabled ? (
                  <span className="text-xs text-red-400">無効</span>
                ) : (
                  <button
                    onClick={() => handleDisable(c.code)}
                    className="text-xs font-medium px-2 py-1 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                    title="無効化"
                  >
                    <Ban className="w-3.5 h-3.5 inline" />
                  </button>
                )}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

/**
 * 管理者ページ本体
 */
export const AdminPage: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<AdminTab>("users");
  const [filter, setFilter] = useState<QuestionFilter>({});
  const [offset, setOffset] = useState(0);
  const [figureFilter, setFigureFilter] = useState<FigureFilter>("all");

  // 図表フィルタの検索ロジック:
  // - "has_image": API側で has_image=true フィルタ（image_url IS NOT NULL）
  // - その他の図表フィルタ: "省略" で検索（選択肢に残っているテキストでマッチ）
  // - "all": ユーザーの検索条件をそのまま使用
  const searchForFigure =
    figureFilter === "all" || figureFilter === "has_image"
      ? filter.search
      : "省略";

  const { questions: rawQuestions, total: rawTotal, isLoading, error, refetch } = useQuestions({
    ...filter,
    search: searchForFigure,
    hasImage: figureFilter === "has_image" || undefined,
    limit: figureFilter === "all" || figureFilter === "has_image" || figureFilter === "no_image"
      ? PAGE_SIZE
      : 100,
    offset: figureFilter !== "all" && figureFilter !== "has_image" && figureFilter !== "no_image"
      ? 0 : offset,
    idToken: user?.idToken,
  });

  // クライアント側でType A/B/画像有無をフィルタ
  const { questions, total } = useMemo(() => {
    if (figureFilter === "all") return { questions: rawQuestions, total: rawTotal };
    if (figureFilter === "figure_all") return { questions: rawQuestions, total: rawTotal };
    if (figureFilter === "has_image") {
      return { questions: rawQuestions, total: rawTotal };
    }
    if (figureFilter === "no_image") {
      const filtered = rawQuestions.filter((q) => hasFigurePlaceholder(q) && !q.imageUrl);
      return { questions: filtered, total: filtered.length };
    }
    if (figureFilter === "figure_a") {
      const filtered = rawQuestions.filter(
        (q) => hasFigurePlaceholder(q) && !choicesAreFigure(q.choices)
      );
      return { questions: filtered, total: filtered.length };
    }
    if (figureFilter === "figure_b") {
      const filtered = rawQuestions.filter(
        (q) => hasFigurePlaceholder(q) && choicesAreFigure(q.choices)
      );
      return { questions: filtered, total: filtered.length };
    }
    return { questions: rawQuestions, total: rawTotal };
  }, [rawQuestions, rawTotal, figureFilter]);

  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  const handleFilterChange = (newFilter: QuestionFilter) => {
    setFilter(newFilter);
    setOffset(0);
  };

  const handleExamTypeChange = (examType: string, section?: string) => {
    if (!examType) {
      handleFilterChange({
        ...filter,
        examCategory: undefined,
        examSection: undefined,
      });
    } else {
      handleFilterChange({
        ...filter,
        examCategory: examType,
        examSection: section,
      });
    }
  };

  if (isLoading && questions.length === 0) {
    return (
      <div className="text-center py-12">
        <Loader2 className="w-10 h-10 text-slate-900 dark:text-slate-100 animate-spin mx-auto mb-3" />
        <p className="text-slate-500 dark:text-slate-400">問題データを読み込み中...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
        <p className="text-slate-900 dark:text-slate-100 font-bold mb-2">読み込みに失敗しました</p>
        <p className="text-slate-500 dark:text-slate-400 mb-4">{error}</p>
        <Button variant="primary" size="md" onClick={refetch}>
          <RefreshCw className="w-4 h-4 mr-2 inline" />
          再試行
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* ナビゲーション: アプリに戻る */}
      <a
        href="/mypage"
        className="inline-flex items-center gap-1 text-sm text-blue-500 hover:text-blue-600 font-medium"
      >
        &larr; アプリに戻る
      </a>

      {/* タブ切替 */}
      <div className="flex gap-1 bg-slate-100 dark:bg-slate-700 p-1 rounded-xl">
        <button
          onClick={() => setActiveTab("users")}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-colors ${
            activeTab === "users"
              ? "bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 shadow-sm"
              : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
          }`}
        >
          <Users className="w-4 h-4" />
          ユーザー / コード
        </button>
        <button
          onClick={() => setActiveTab("questions")}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-colors ${
            activeTab === "questions"
              ? "bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 shadow-sm"
              : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
          }`}
        >
          <Crown className="w-4 h-4" />
          問題データ
        </button>
        <button
          onClick={() => setActiveTab("feedback")}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-colors ${
            activeTab === "feedback"
              ? "bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 shadow-sm"
              : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
          }`}
        >
          <Flag className="w-4 h-4" />
          品質報告
        </button>
        <button
          onClick={() => setActiveTab("generated")}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-colors ${
            activeTab === "generated"
              ? "bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 shadow-sm"
              : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
          }`}
        >
          <Sparkles className="w-4 h-4" />
          生成問題
        </button>
      </div>

      {/* ユーザー / コード管理タブ */}
      {activeTab === "users" && (
        <div className="space-y-6">
          <UserManagementSection />
          <InviteCodeSection />
        </div>
      )}

      {/* 問題データ管理タブ */}
      {activeTab === "questions" && (
      <div className="space-y-4">

      {/* タイトル */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">問題データ管理</h2>
        <span className="text-sm text-slate-500 dark:text-slate-400">
          全 <span className="font-bold text-slate-900 dark:text-slate-100">{total}</span> 問
        </span>
      </div>

      {/* フィルター */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-600 p-4 shadow-sm">
        <div className="flex flex-wrap items-center gap-3">
          {/* 試験区分 */}
          <div className="flex items-center gap-1">
            <span className="text-xs text-slate-500 dark:text-slate-400 mr-1">試験</span>
            {EXAM_TYPES.map((e) => (
              <button
                key={e.value}
                onClick={() => handleExamTypeChange(e.value, e.section)}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                  (e.value === "" && !filter.examCategory) ||
                  filter.examCategory === e.value
                    ? "bg-slate-900 dark:bg-slate-100 dark:bg-slate-700 text-white dark:text-slate-900 dark:text-slate-100"
                    : "bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600"
                }`}
              >
                {e.label}
              </button>
            ))}
          </div>

          <div className="hidden sm:block w-px h-5 bg-slate-200 dark:bg-slate-600" />

          {/* 年度 */}
          <div className="flex items-center gap-1">
            <span className="text-xs text-slate-500 dark:text-slate-400 mr-1">年度</span>
            <button
              onClick={() => handleFilterChange({ ...filter, year: undefined })}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                !filter.year
                  ? "bg-slate-900 dark:bg-slate-100 dark:bg-slate-700 text-white dark:text-slate-900 dark:text-slate-100"
                  : "bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600"
              }`}
            >
              すべて
            </button>
            {YEARS.map((y) => (
              <button
                key={y}
                onClick={() =>
                  handleFilterChange({
                    ...filter,
                    year: filter.year === y ? undefined : y,
                  })
                }
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                  filter.year === y
                    ? "bg-slate-900 dark:bg-slate-100 dark:bg-slate-700 text-white dark:text-slate-900 dark:text-slate-100"
                    : "bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600"
                }`}
              >
                {y}
              </button>
            ))}
          </div>

          <div className="hidden sm:block w-px h-5 bg-slate-200 dark:bg-slate-600" />

          {/* 図表 */}
          <div className="flex items-center gap-1">
            <span className="text-xs text-slate-500 dark:text-slate-400 mr-1">図表</span>
            {([
              { value: "all", label: "すべて" },
              { value: "figure_all", label: "図表あり" },
              { value: "figure_a", label: "Type A" },
              { value: "figure_b", label: "Type B" },
              { value: "has_image", label: "画像済" },
              { value: "no_image", label: "画像なし" },
            ] as { value: FigureFilter; label: string }[]).map((f) => (
              <button
                key={f.value}
                onClick={() => { setFigureFilter(f.value); setOffset(0); }}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                  figureFilter === f.value
                    ? f.value === "figure_b"
                      ? "bg-orange-500 text-white"
                      : f.value === "no_image"
                        ? "bg-red-500 text-white"
                        : "bg-slate-900 dark:bg-slate-100 dark:bg-slate-700 text-white dark:text-slate-900 dark:text-slate-100"
                    : "bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          <div className="hidden sm:block w-px h-5 bg-slate-200 dark:bg-slate-600" />

          {/* 季節 */}
          <div className="flex items-center gap-1">
            <span className="text-xs text-slate-500 dark:text-slate-400 mr-1">季節</span>
            <button
              onClick={() =>
                handleFilterChange({ ...filter, season: undefined })
              }
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                !filter.season
                  ? "bg-slate-900 dark:bg-slate-100 dark:bg-slate-700 text-white dark:text-slate-900 dark:text-slate-100"
                  : "bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600"
              }`}
            >
              すべて
            </button>
            {SEASONS.map((s) => (
              <button
                key={s.value}
                onClick={() =>
                  handleFilterChange({
                    ...filter,
                    season: filter.season === s.value ? undefined : s.value,
                  })
                }
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                  filter.season === s.value
                    ? "bg-slate-900 dark:bg-slate-100 dark:bg-slate-700 text-white dark:text-slate-900 dark:text-slate-100"
                    : "bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* テーブルヘッダー */}
      <div className="hidden md:flex items-center gap-3 px-4 py-2 text-xs font-bold text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-600">
        <span className="w-8 text-right">#</span>
        <span className="w-48">ID</span>
        <span className="flex-1">問題文</span>
        <span className="w-10 text-center">正解</span>
        <span className="w-12 text-center">基本</span>
        <span className="w-12 text-center">詳細</span>
        <span className="w-10 text-center">画像</span>
        <span className="w-4" />
      </div>

      {/* 問題一覧 */}
      <div className="space-y-1">
        {questions.map((q, i) => (
          <QuestionRow key={q.id} question={q} index={offset + i} defaultExpanded={figureFilter === "has_image"} />
        ))}
      </div>

      {/* ページネーション */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 py-4">
          <button
            onClick={() => { setOffset(Math.max(0, offset - PAGE_SIZE)); window.scrollTo(0, 0); }}
            disabled={offset === 0}
            className="p-2 rounded-lg border border-slate-200 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>

          <span className="text-sm text-slate-700 dark:text-slate-300 px-3">
            {currentPage} / {totalPages} ページ
          </span>

          <button
            onClick={() => {
              setOffset(Math.min((totalPages - 1) * PAGE_SIZE, offset + PAGE_SIZE));
              window.scrollTo(0, 0);
            }}
            disabled={currentPage >= totalPages}
            className="p-2 rounded-lg border border-slate-200 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      </div>
      )}

      {/* 品質報告タブ */}
      {activeTab === "feedback" && <FeedbackSection />}

      {/* 生成問題タブ（資格拡張エンジン L-6・design.md §6b） */}
      {activeTab === "generated" && <GeneratedPanel />}
    </div>
  );
};

const CATEGORY_LABELS: Record<string, string> = {
  wrong_answer: "正解が違う",
  wrong_explanation: "解説に誤り",
  wrong_question: "問題文・選択肢",
  other: "その他",
};

const FeedbackSection: React.FC = () => {
  const { user } = useAuth();
  const [items, setItems] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [statusFilter, setStatusFilter] = React.useState("open");

  const fetchFeedback = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/feedback?status=${statusFilter}&limit=100`,
        {
          headers: {
            Authorization: `Bearer ${user?.idToken}`,
            "Content-Type": "application/json",
          },
        },
      );
      const json = await res.json();
      setItems(json.data?.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [user?.idToken, statusFilter]);

  React.useEffect(() => {
    fetchFeedback();
  }, [fetchFeedback]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">品質報告一覧</h2>
        <div className="flex gap-2">
          {["open", "resolved", "dismissed"].map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                statusFilter === s
                  ? "bg-slate-900 dark:bg-slate-100 dark:bg-slate-700 text-white dark:text-slate-900 dark:text-slate-100"
                  : "bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600"
              }`}
            >
              {s === "open" ? "未対応" : s === "resolved" ? "対応済み" : "却下"}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 text-slate-400 dark:text-slate-500">
          報告はありません
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((item: any) => (
            <div
              key={item.id}
              className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-600 p-4 shadow-sm"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                      {CATEGORY_LABELS[item.category] || item.category}
                    </span>
                    <span className="text-xs text-slate-400 dark:text-slate-500">
                      {item.exam_type}
                    </span>
                  </div>
                  <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                    {item.question_id}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 line-clamp-2">
                    {item.question_text?.slice(0, 100)}
                  </p>
                  {item.comment && (
                    <p className="text-xs text-slate-700 dark:text-slate-300 mt-2 bg-slate-50 dark:bg-slate-900 p-2 rounded-lg">
                      {item.comment}
                    </p>
                  )}
                </div>
                <span className="text-xs text-slate-400 dark:text-slate-500 whitespace-nowrap">
                  {new Date(item.created_at).toLocaleDateString("ja-JP")}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminPage;
